import { useState, useEffect } from 'react';
import { X, Shield, Award, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useGeolocation } from '@/hooks/useGeolocation';
import { submitLead, handleLeadResponse, getDialCodeFromCountry } from '@/utils/apiService';

const ContactPopup = ({ isOpen, onClose, pageSource = 'main' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country_code: '+1',
    message: '',
    page_source: pageSource
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const { geoData } = useGeolocation();

  // Auto-populate country code from geolocation
  useEffect(() => {
    if (geoData?.data?.countryCode) {
      const dialCode = getDialCodeFromCountry(geoData.data.countryCode);
      if (dialCode) {
        setFormData(prev => ({ ...prev, country_code: dialCode }));
      }
    }
  }, [geoData]);

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    
    // Only allow numbers
    const numbersOnly = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limitedValue = numbersOnly.slice(0, 10);
    
    // Update form data
    setFormData({ ...formData, phone: limitedValue });
    
    // Validation feedback
    if (value !== numbersOnly) {
      setPhoneError('Only numbers are allowed');
    } else if (numbersOnly.length > 10) {
      setPhoneError('Maximum 10 digits allowed');
    } else if (numbersOnly.length > 0 && numbersOnly.length < 10) {
      setPhoneError('Phone number must be 10 digits');
    } else {
      setPhoneError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (phoneError) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Submit lead with geolocation data
      const response = await submitLead(formData, geoData);
      
      // Handle response (redirect or show error)
      const success = handleLeadResponse(response, setSubmitError);
      
      if (success) {
        // Form will redirect, no need to reset
        return;
      }
    } catch (error) {
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="contact-popup">
      <div className="bg-white rounded-2xl max-w-5xl w-full relative animate-fade-in-up md:my-8 max-h-[90vh] md:max-h-[95vh] flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 md:top-4 md:right-4 text-gray-400 hover:text-primary-600 z-10 bg-white rounded-full p-1 shadow-lg"
          data-testid="popup-close-btn"
        >
          <X className="w-5 h-5 md:w-6 md:h-6" />
        </button>

        <div className="grid md:grid-cols-2 h-full overflow-hidden">
          {/* Left Side - Image & Info - Hidden on mobile */}
          <div className="hidden md:block relative bg-slate-900 rounded-l-2xl p-8 md:p-12 overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(124,58,237,0.2)_25%,rgba(124,58,237,0.2)_50%,transparent_50%,transparent_75%,rgba(139,92,246,0.2)_75%)] bg-[size:60px_60px]"></div>
            </div>
            
            <div className="relative z-10 flex flex-col justify-center h-full">
              <div className="mb-6">
                <span className="bg-primary-600 text-white px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide">
                  Free Consultation
                </span>
              </div>
              
              <h2 className="text-4xl font-bold mb-4 leading-tight text-white">
                Protect Your Brand <span className="bg-primary-600 bg-clip-text text-transparent">Today</span>
              </h2>
              
              <p className="text-gray-300 mb-8 leading-relaxed">
                Get expert legal advice from our experienced trademark attorneys. We'll help you secure and protect your intellectual property.
              </p>

              {/* Trust Indicators */}
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 text-white">High Success Rate</h3>
                    <p className="text-sm text-gray-400">Industry-leading approval rate</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-secondary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 text-white">Thousands of Clients</h3>
                    <p className="text-sm">Trusted by businesses nationwide</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 text-white">24-Hour Response</h3>
                    <p className="text-sm text-gray-400">Quick turnaround guaranteed</p>
                  </div>
                </div>
              </div>

              {/* Decorative Element */}
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary-600 rounded-full blur-3xl"></div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="p-4 md:p-12 overflow-y-auto">
            {/* Mobile Header - More Compact */}
            <div className="mb-3 md:mb-6 md:hidden text-center">
              <span className="inline-block bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide mb-2">
                Free Consultation
              </span>
              <h2 className="text-lg font-bold text-gray-900">Protect Your Brand Today</h2>
            </div>

            <h3 className="hidden md:block text-xl md:text-2xl font-bold mb-2 text-gray-900">Get Started</h3>
            <p className="hidden md:block text-sm md:text-base text-gray-600 mb-6">Fill out the form and we'll contact you within 24 hours.</p>

            <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
          {submitError && (
            <div className="bg-primary-100 border border-primary-600 text-primary-600 px-4 py-3 rounded-lg text-sm">
              {submitError}
            </div>
          )}
          
          <div>
            <Label htmlFor="name" className="text-xs md:text-sm">Name *</Label>
            <Input
              id="name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="John Doe"
              className="mt-1 h-9 md:h-10 text-sm"
              data-testid="popup-name-input"
            />
          </div>

          <div>
            <Label htmlFor="email" className="text-xs md:text-sm">Email *</Label>
            <Input
              id="email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="john@example.com"
              className="mt-1 h-9 md:h-10 text-sm"
              data-testid="popup-email-input"
            />
          </div>

          <div>
            <Label htmlFor="phone" className="text-xs md:text-sm">Contact Number *</Label>
            <div className="flex space-x-2 mt-1">
              <Select
                value={formData.country_code}
                onValueChange={(value) => setFormData({ ...formData, country_code: value })}
              >
                <SelectTrigger className="w-20 h-9 md:h-10 text-sm" data-testid="popup-country-code-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4} className="z-[9999] max-h-60">
                  <SelectItem value="+1">+1 (US)</SelectItem>
                  <SelectItem value="+44">+44 (UK)</SelectItem>
                  <SelectItem value="+91">+91 (IN)</SelectItem>
                  <SelectItem value="+61">+61 (AU)</SelectItem>
                  <SelectItem value="+49">+49 (DE)</SelectItem>
                  <SelectItem value="+33">+33 (FR)</SelectItem>
                  <SelectItem value="+81">+81 (JP)</SelectItem>
                  <SelectItem value="+86">+86 (CN)</SelectItem>
                  <SelectItem value="+82">+82 (KR)</SelectItem>
                  <SelectItem value="+55">+55 (BR)</SelectItem>
                  <SelectItem value="+52">+52 (MX)</SelectItem>
                  <SelectItem value="+39">+39 (IT)</SelectItem>
                  <SelectItem value="+34">+34 (ES)</SelectItem>
                  <SelectItem value="+31">+31 (NL)</SelectItem>
                  <SelectItem value="+46">+46 (SE)</SelectItem>
                  <SelectItem value="+41">+41 (CH)</SelectItem>
                  <SelectItem value="+65">+65 (SG)</SelectItem>
                  <SelectItem value="+971">+971 (AE)</SelectItem>
                  <SelectItem value="+27">+27 (ZA)</SelectItem>
                  <SelectItem value="+64">+64 (NZ)</SelectItem>
                  <SelectItem value="+92">+92 (PK)</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex-1">
                <Input
                  id="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  placeholder="1234567890"
                  maxLength={10}
                  className={`h-9 md:h-10 text-sm ${phoneError ? 'border-primary-600' : ''}`}
                  data-testid="popup-phone-input"
                />
                {phoneError && (
                  <p className="text-primary-600 text-xs mt-1">{phoneError}</p>
                )}
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="message" className="text-xs md:text-sm">Message *</Label>
            <Textarea
              id="message"
              required
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Tell us about your trademark needs..."
              className="mt-1 text-sm resize-none"
              rows={3}
              data-testid="popup-message-input"
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting || phoneError}
            className="w-full bg-secondary-600 text-white py-2.5 md:py-3 rounded-full font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
            data-testid="popup-submit-btn"
          >
            {isSubmitting ? 'Submitting...' : 'Register Your Trademark'}
          </Button>
          
          <p className="text-xs text-gray-500 text-center mt-2 md:mt-4">
            🔒 Your information is secure and confidential
          </p>
        </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPopup;